import { components } from "@/api-schema";
import { Can } from "@/casl-provider";
import { VehicleSearchBody } from "@/common/api/vehicle/use-vehicle-search.query";
import { getVehicleVinValidation, normalizeVehicleVin } from "@/common/helpers/vehicle-vin";
import { useDevice } from "@/common/hooks/use-device";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { VehicleTable } from "@/components/data-table/predefined/vehicle-table";
import { vehicleTableDefaultColumns } from "@/components/data-table/predefined/vehicle-table-columns";
import { DataTableHeightMode, DataTableRow, ImplementedColumnDef } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelGenerationFilter } from "@/components/filter/predefined/model-generation-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { VehicleVinFieldStatus } from "@/components/vehicle-vin/vehicle-vin-field-status";
import { CalculatorIcon, ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

type Vehicle = components["schemas"]["Vehicle"] & RowWithIdentifier;

const VEHICLE_VALUATION_INITIALIZE_VEHICLE_COLUMNS = [
  vehicleTableDefaultColumns.mobile.columnMobileCompleteWithoutTransportStatus,
  vehicleTableDefaultColumns.desktopAndTablet.columnBrandModelDescription,
  vehicleTableDefaultColumns.desktopAndTablet.columnVinColor,
  vehicleTableDefaultColumns.widescreen.columnBrand,
  vehicleTableDefaultColumns.widescreen.columnModelDescription,
  vehicleTableDefaultColumns.widescreen.columnVin,
  vehicleTableDefaultColumns.widescreen.columnColor,
  vehicleTableDefaultColumns.widescreen.columnRegistrationDate,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnOwningBranch,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnCurrentBranch,
] as ImplementedColumnDef<Vehicle, unknown>[];

export function VehicleValuationSelectVehicleView() {
  const { t } = useTranslation(["common", "masterdata", "fab"]);
  const [_, navigate] = useLocation();
  const { isMobileOrTablet } = useDevice();

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    branches: [],
    brands: [],
    modelGenerations: [],
  });
  const [isVehicleListOpen, setIsVehicleListOpen] = useState(false);
  const [vin, setVin] = useState("");
  const [vinTouched, setVinTouched] = useState(false);

  const normalizedVin = normalizeVehicleVin(vin);
  const vinValidation = getVehicleVinValidation(vin, vinTouched);

  const baseBody = useMemo((): VehicleSearchBody => {
    return {
      currentBranch: filterValues.branches?.length > 0 ? filterValues.branches : [],
      ...(filterValues.brands?.length > 0 && { brand: filterValues.brands }),
      ...(filterValues.modelGenerations?.length > 0 && { modelGeneration: filterValues.modelGenerations }),
    } as VehicleSearchBody;
  }, [filterValues.branches, filterValues.brands, filterValues.modelGenerations]);

  const filters = useMemo(
    () => [
      new BranchFilter().setLabelOverrides({
        labelSingular: t("Location"),
        labelPlural: t("Locations"),
        placeholder: t("fab:SelectLocations"),
        searchPlaceholder: t("fab:SearchLocations"),
        emptyText: t("fab:NoLocationsFound"),
      }),
      new BrandFilter(),
      new ModelGenerationFilter({ body: { brand: filterValues.brands || [] } }),
    ],
    [filterValues.brands, t],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => {
      const newValues = {
        ...prev,
        [filterId]: values,
      };

      if (filterId === "brands") {
        newValues.modelGenerations = [];
      }

      return newValues;
    });
  }, []);

  const navigateToVehicleValuationCreate = useCallback(
    (vehicle: Vehicle) => {
      navigate(`~/fab/list/create?vehicleUuid=${vehicle.uuid}`);
    },
    [navigate],
  );

  const navigateToVehicleValuationCreateByVin = useCallback(() => {
    if (!vinValidation.isValid) {
      return;
    }

    navigate(`~/fab/list/create?vin=${encodeURIComponent(normalizedVin)}`);
  }, [navigate, normalizedVin, vinValidation.isValid]);

  const customRowActions = useCallback(
    (row: DataTableRow<Vehicle>) => {
      return [
        {
          label: t("fab:StartVehicleValuation"),
          icon: <CalculatorIcon className="h-4 w-4" />,
          onClick: () => navigateToVehicleValuationCreate(row.original),
        },
      ];
    },
    [navigateToVehicleValuationCreate, t],
  );

  const handleRowClick = useCallback(
    (vehicle: Vehicle) => {
      navigateToVehicleValuationCreate(vehicle);
    },
    [navigateToVehicleValuationCreate],
  );

  return (
    <Can I="create" a="VehicleValuation">
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("fab:SelectVehicleForValuation")}
        subTitle={t("fab:SelectVehicleForValuationDescription")}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <Card className="shrink-0">
            <CardHeader>
              <CardTitle>{t("fab:VehicleValuationVinEntryTitle")}</CardTitle>
              <CardDescription>{t("fab:VehicleValuationVinEntryDescription")}</CardDescription>
            </CardHeader>

            <CardContent>
              <form
                className="flex flex-col gap-3 md:flex-row md:items-start"
                onSubmit={(event) => {
                  event.preventDefault();
                  navigateToVehicleValuationCreateByVin();
                }}
              >
                <div className="flex-1 space-y-2">
                  <Input
                    value={vin}
                    maxLength={17}
                    error={vinValidation.hasError}
                    onChange={(event) => setVin(event.target.value.toUpperCase())}
                    onBlur={() => setVinTouched(true)}
                    placeholder={t("masterdata:VehicleVinPlaceholder")}
                  />
                  {normalizedVin.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t("fab:VehicleValuationVinEntryHint")}</p>
                  ) : (
                    <VehicleVinFieldStatus value={vin} showLengthError={vinTouched} />
                  )}
                </div>

                <Button type="submit" disabled={!vinValidation.isValid} className="md:self-start">
                  {t("fab:StartVehicleValuationByVin")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Collapsible
            open={isVehicleListOpen}
            onOpenChange={setIsVehicleListOpen}
            className="mt-1 flex min-h-0 flex-1 flex-col gap-3"
          >
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Sm}
                className="h-auto w-fit self-start rounded-md px-2 py-1"
              >
                {isVehicleListOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {isVehicleListOpen ? t("fab:HideVehicles") : t("fab:ShowVehicles")}
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="min-h-0 overflow-hidden data-[state=closed]:hidden data-[state=open]:flex-1">
              <div className="flex h-full min-h-0 flex-1 flex-col">
                <VehicleTable
                  columns={VEHICLE_VALUATION_INITIALIZE_VEHICLE_COLUMNS}
                  body={baseBody}
                  filters={filters}
                  filterValues={filterValues}
                  onFilterChange={handleFilterChange}
                  customRowActions={isMobileOrTablet ? undefined : customRowActions}
                  onRowClick={handleRowClick}
                  features={{ expansion: false }}
                  heightMode={DataTableHeightMode.Viewport}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CenteredLayout>
    </Can>
  );
}
